<script setup lang="ts" generic="R extends Record<string, unknown>">
/*
 * Plain prose table matching the prototype's .tbl class. For sortable /
 * paginated / virtualized lists, build on top of this with composables —
 * this component is intentionally simple.
 */
import { computed, getCurrentInstance } from 'vue';

interface Column<T> {
  key: string;
  label: string;
  width?: string;
  align?: 'left' | 'right' | 'center';
  /** Format the cell content; default = String(row[key]). */
  format?: (row: T) => string | number;
}

defineProps<{
  columns: Column<R>[];
  rows: R[];
  /** Optional row click handler — toggles the prototype's `.row-link` hover. */
  rowKey?: keyof R;
  /** Optional per-row CSS class function — return a Tailwind class string or undefined. */
  rowClass?: (row: R) => string | undefined;
}>();

defineEmits<{ rowClick: [row: R] }>();

// A `@row-click` listener from the parent is a DECLARED emit (see defineEmits),
// so Vue strips it out of `$attrs` — the old `$attrs.onRowClick` check could
// never see it, which is why the pointer cursor + hover never appeared. Read
// the raw vnode props instead, which still carry `onRowClick` regardless of
// the emit declaration, so the row reliably gets the clickable affordance.
const instance = getCurrentInstance();
const isClickable = computed(() => {
  const vnodeProps = (instance?.vnode.props ?? {}) as Record<string, unknown>;
  return Boolean(vnodeProps.onRowClick ?? vnodeProps.onrowclick ?? vnodeProps['onRow-click']);
});

const align = (a?: 'left' | 'right' | 'center') =>
  a === 'right' ? 'text-right' : a === 'center' ? 'text-center' : 'text-left';
</script>

<template>
  <div class="overflow-hidden rounded-3xl border border-app-border bg-app-surface">
    <!-- Optional header band above the column row (the slot owns its bg/padding).
         Stays OUTSIDE the scroll area so it doesn't scroll with the rows. -->
    <div v-if="$slots.caption" class="border-b border-app-border">
      <slot name="caption" />
    </div>
    <!-- Scroll container: vertical (within max-height) + horizontal (when the
         columns overflow). Sticky <thead> keeps headers visible. The wrapper
         card owns the rounded corners; this inner div owns the overflow so the
         corners clip without clipping the scroll. -->
    <div class="tbl-scroll" data-testid="ui-table-scroll">
      <table class="w-full min-w-max border-collapse text-md">
        <thead class="bg-slate-50">
          <tr>
            <th
              v-for="c in columns"
              :key="c.key"
              :class="[
                'sticky top-0 z-[1] border-b border-app-border bg-slate-50 px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-app-muted',
                align(c.align),
              ]"
              :style="c.width ? { width: c.width } : undefined"
              scope="col"
            >
              {{ c.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in rows"
            :key="rowKey ? String(row[rowKey]) : idx"
            :class="[
              'border-b border-app-border-soft transition-colors last:border-b-0',
              rowClass ? rowClass(row) : undefined,
              isClickable ? 'cursor-pointer hover:bg-primary-50/60' : '',
            ]"
            :data-clickable="isClickable ? 'true' : undefined"
            @click="$emit('rowClick', row)"
          >
            <td
              v-for="c in columns"
              :key="c.key"
              :class="['px-4 py-3 text-app-fg', align(c.align)]"
            >
              <slot :name="`cell:${c.key}`" :row="row" :value="row[c.key]">
                {{ c.format ? c.format(row) : String(row[c.key] ?? '') }}
              </slot>
            </td>
          </tr>
          <tr v-if="rows.length === 0">
            <td :colspan="columns.length" class="px-4 py-12 text-center text-md text-app-muted">
              <slot name="empty">No data</slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
